import * as React from 'react';
import { styled } from '@glitz/react';
import Link from 'webapp/components/Link';
import { linkTitle } from 'webapp/product/product-seo-info';
import { SquareImage } from '../image';
import { overflowEllipsisDecorator } from 'webapp/decorators/text';
import { black } from 'webapp/shared/sharedTheme';
import { StockStatus } from 'nordicnest/product/StockStatus';
import Price from 'nordicnest/product/Price';
import { Variant } from 'webapp/product/price-variant';
import { WishlistButton } from 'webapp/components/FavoriteButton';
import { AddToCartButton } from 'webapp/components/AddToCartButton';

export default function ShopTheLookProductCard(product: NordicNest.Api.PartialProductApiModel) {
  const [validationMessage, setValidationMessage] = React.useState<string>();

  return (
    <Card>
      <ProductLink title={linkTitle(product)} to={product.url}>
        <Image
          alt={linkTitle(product)}
          image={product.images[0]}
          sizes="40vw"
          preset={'tiny'}
          imageCss={imageDecorator}
        />
      </ProductLink>
      <ProductInfo>
        <ProductLink title={linkTitle(product)} to={product.url}>
          <Brand>{product.brand}</Brand>
          <Title>{product.displayName}</Title>
          {product.availableInCountry && (
            <StockStatus productCardStyling variantStatus={product.variantStatus} stockStatus={product.stockStatus} />
          )}
        </ProductLink>
        <ProductPriceWrapper>
          <ProductPrice
            variant={Variant.Compact}
            price={product.price}
            config={product.price}
            useOrigianlPricePrefixSuffix
          />
          <Buttons>
            <FavoriteButton product={product} location="shop-the-look" name="add-to-wishlist" />
            {(!product.isPackage || product.quickBuyPackage !== null) && product.availableInCountry && (
              <AddToCartButton product={product} setValidationMessage={setValidationMessage} />
            )}
          </Buttons>
        </ProductPriceWrapper>
        {validationMessage && <ValidationMessage>{validationMessage}</ValidationMessage>}
      </ProductInfo>
    </Card>
  );
}

const Card = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: t => t.sharedSpacing.m,
});

const ProductLink = styled(Link, {
  flexGrow: 0,
  flexShrink: 0,
});

const Image = styled(SquareImage, {
  width: 112,
  height: 112,
  position: 'relative',
  padding: { xy: 0 },
  ':after': {
    content: "''",
    backgroundColor: 'rgba(128, 122, 106, 0.06)',
    position: 'absolute',
    right: 0,
    left: 0,
    bottom: 0,
    top: 0,
  },
});

const imageDecorator = styled({
  padding: { xy: t => t.sharedSpacing.m },
});

const ProductInfo = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: t => t.sharedSpacing.xs,
  margin: { y: t => t.sharedSpacing.xs },
  minWidth: 0,
  width: '100%',
});

const Brand = styled.div(overflowEllipsisDecorator, {
  fontSize: t => t.productCard?.brand?.fontSize,
  fontWeight: t => t.productCard?.brand?.fontWeight,
  color: black,
  lineHeight: '18px',
});

const Title = styled.h3(overflowEllipsisDecorator, {
  fontSize: t => t.productCard?.title?.fontSize,
  fontWeight: t => t.fontWeight?.normal,
  color: black,
  lineHeight: '18px',
  marginBottom: t => t.sharedSpacing.xxs,
});

const ProductPriceWrapper = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  gap: t => t.sharedSpacing.m,
});

const ProductPrice = styled(Price, {
  fontSize: t => t.sharedFontSize.xs,
  flexWrap: 'wrap',
});

const Buttons = styled.div({
  display: 'flex',
  gap: 10,
});

const FavoriteButton = styled(WishlistButton, {
  padding: { xy: 0 },
  fontSize: t => t.sharedFontSize.xxs,
  border: { xy: { width: 0 } },
});

const ValidationMessage = styled.div({
  color: t => t.negative.color,
  fontSize: t => t.sharedFontSize.xxs,
  fontWeight: t => t.fontWeight?.bold,
});
